import React, {useMemo, useState} from 'react'
import {generatePath} from 'react-router'
import {Link as RouterLink} from 'react-router-dom'
import {BoxLink} from '../../common/router_links/BoxLink'
import {Routes} from '../../common/Routes'
import {Organisation} from '../../common/types/organisation'
import {Box} from '../../webui/Box'
import {Link} from '../../webui/Link'
import {SortablePaginatedTable} from '../../webui/table/sortable-paginated-table/SortablePaginatedTable'
import {TableHeadCell} from '../../webui/table/sortable-paginated-table/TableHeadCell'
import {Comparator, getComparator} from '../../webui/table/sortable-paginated-table/utils'
import {GQL_ContractType} from '../../generated/graphql'
import {Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent} from '@mui/material'
import {CONTRACT_TYPE_ORDER, contractTypeLabel} from '../../common/domain/contractType'

interface Props {
  organisations: Organisation[]
}

export interface OrganisationsData {
  archivedAt?: string | null
  boxes: Organisation['boxes']
  city: Organisation['city']
  contractTypes: Organisation['contractTypes']
  id: string
  name: string
  numberOfBoxes: number
}

const formatContractTypes = (types: GQL_ContractType[]): string => {
  if (!types || types.length === 0) return '—'

  const set = new Set(types)
  const ordered = CONTRACT_TYPE_ORDER.filter((t) => set.has(t))
  return ordered.map(contractTypeLabel).join(', ')
}

const cellStyle = (row: OrganisationsData): React.CSSProperties => {
  const isArchived = (row.archivedAt ?? null) !== null
  return isArchived ? {opacity: 0.6, fontStyle: 'italic'} : {}
}

const archivedBadge = (row: OrganisationsData) => {
  const isArchived = (row.archivedAt ?? null) !== null
  if (!isArchived) return null

  return (
    <Box
      component="span"
      ml={1}
      px={1}
      py={0.25}
      sx={{
        border: '1px solid rgba(0,0,0,0.25)',
        borderRadius: 999,
        fontSize: 12,
        lineHeight: 1.4,
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      Archived
    </Box>
  )
}

const organisationHeadCells: TableHeadCell<OrganisationsData>[] = [
  {
    id: 'name',
    label: 'Name',
    render: (row) => (
      <Box component="span" style={cellStyle(row)} title={(row.archivedAt ?? null) !== null ? `Archived at: ${row.archivedAt}` : undefined}>
        <Link
          component={RouterLink}
          to={generatePath(Routes.ORGANISATION, {
            id: row['id'],
          })}
        >
          {row['name']}
        </Link>
        {archivedBadge(row)}
      </Box>
    ),
  },
  {
    id: 'city',
    label: 'City',
    render: (row) => (
      <Box style={cellStyle(row)} title={(row.archivedAt ?? null) !== null ? `Archived at: ${row.archivedAt}` : undefined}>
        {row.city ?? '—'}
      </Box>
    ),
  },
  {
    id: 'boxes',
    label: 'Boxes',
    render: (row) => (
      <Box display={'flex'} style={cellStyle(row)} title={(row.archivedAt ?? null) !== null ? `Archived at: ${row.archivedAt}` : undefined}>
        {row.boxes.map((box, index) => (
          <Box display={'flex'} key={box.id} ml={index !== 0 ? 2 : 0}>
            <BoxLink boxId={box.id} linkText={box.name} />
          </Box>
        ))}
      </Box>
    ),
  },
  {
    id: 'numberOfBoxes',
    label: 'Number of boxes',
    render: (row) => (
      <Box style={cellStyle(row)} title={(row.archivedAt ?? null) !== null ? `Archived at: ${row.archivedAt}` : undefined}>
        {row.numberOfBoxes}
      </Box>
    ),
  },
  {
    id: 'contractTypes',
    label: 'Contract types',
    render: (row) => (
      <Box style={cellStyle(row)} title={(row.archivedAt ?? null) !== null ? `Archived at: ${row.archivedAt}` : undefined}>
        {formatContractTypes(row.contractTypes)}
      </Box>
    ),
  },
]

const createRows = (organisations: Organisation[]): OrganisationsData[] => {
  return organisations.map((organisation) => ({
    archivedAt: organisation.archivedAt ?? null,
    boxes: organisation.boxes,
    city: organisation.city,
    contractTypes: organisation.contractTypes,
    id: organisation.id,
    name: organisation.name,
    numberOfBoxes: organisation.boxes.length,
  }))
}

const organisationComparator: Comparator<OrganisationsData> = (order, orderBy) => {
  return getComparator(order, orderBy)
}

export const AllOrganisationsTable: React.FC<Props> = ({organisations}) => {
  const [selectedContractTypes, setSelectedContractTypes] = useState<GQL_ContractType[]>([])

  const filteredOrganisations = useMemo(() => {
    if (selectedContractTypes.length === 0) return organisations

    const wanted = new Set(selectedContractTypes)
    return organisations.filter((o) => o.contractTypes.some((t) => wanted.has(t)))
  }, [organisations, selectedContractTypes])

  const onContractTypesChange = (e: SelectChangeEvent<GQL_ContractType[]>) => {
    const value = e.target.value as unknown as GQL_ContractType[]
    setSelectedContractTypes(value)
  }

  return (
    <Box>
      <Box px={2} pt={2}>
        <FormControl size="small" sx={{minWidth: 320}}>
          <InputLabel id="org-contract-types-filter-label">Filter by contract type</InputLabel>
          <Select
            labelId="org-contract-types-filter-label"
            id="org-contract-types-filter"
            multiple
            value={selectedContractTypes as any}
            onChange={onContractTypesChange as any}
            label="Filter by contract type"
            renderValue={(selected) => {
              const list = (selected as unknown as GQL_ContractType[]) ?? []
              if (list.length === 0) return 'All'
              const set = new Set(list)
              const ordered = CONTRACT_TYPE_ORDER.filter((t) => set.has(t))
              return ordered.map(contractTypeLabel).join(', ')
            }}
          >
            {CONTRACT_TYPE_ORDER.map((t) => (
              <MenuItem key={t} value={t}>
                <Checkbox checked={selectedContractTypes.indexOf(t) > -1} />
                <ListItemText primary={contractTypeLabel(t)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <SortablePaginatedTable
        getComparator={organisationComparator}
        title={'Organisations'}
        initialRowsPerPage={filteredOrganisations.length || 1}
        initialOrderByKey={'name'}
        rows={createRows(filteredOrganisations)}
        tableHeadCells={organisationHeadCells}
        maxTableWidth={1600}
        fixedLayout
      />
    </Box>
  )
}
